import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart, Line } from 'recharts';

import type { BranchAnalysisData } from '../../types/report';

interface BranchSalesChartProps {
  data: BranchAnalysisData[];
}

const formatCurrency = (value: number) => {
  if (value >= 100000000) {
    return `${(value / 100000000).toFixed(1)}억`;
  }
  if (value >= 10000) {
    return `${(value / 10000).toFixed(0)}만`;
  }
  return value.toLocaleString();
};

export const BranchSalesChart = ({ data }: BranchSalesChartProps) => {
  const top10Data = data.slice(0, 10).map((item) => ({
    name: item.branchName.replace('점', ''),
    판매가: item.totalSalePrice,
    기본가: item.totalBasePrice,
    할인율: item.avgDiscountRate,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h4 className="mb-4 text-sm font-medium text-gray-700">상위 10개 지점 매출 현황</h4>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={top10Data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
              <YAxis yAxisId="left" tickFormatter={formatCurrency} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 50]} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name) => {
                  const numValue = Number(value);
                  if (name === '할인율') return `${numValue.toFixed(2)}%`;
                  return formatCurrency(numValue);
                }}
              />
              <Legend wrapperStyle={{ paddingTop: 10 }} />
              <Bar yAxisId="left" dataKey="기본가" name="기본가" fill="#f97316" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="판매가" name="판매가" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="할인율" name="할인율" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500">지점명</th>
              <th className="px-3 py-2 text-right font-medium text-gray-500">평균 유닛가</th>
              <th className="px-3 py-2 text-right font-medium text-gray-500">할인율</th>
              <th className="px-3 py-2 text-right font-medium text-gray-500">케어 매출</th>
              <th className="px-3 py-2 text-right font-medium text-gray-500">패밀리 매출</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.slice(0, 10).map((item) => (
              <tr key={item.branchName}>
                <td className="px-3 py-2 font-medium">{item.branchName}</td>
                <td className="px-3 py-2 text-right">{item.avgUnitPrice.toLocaleString()}원</td>
                <td className="px-3 py-2 text-right">
                  <span className={item.avgDiscountRate > 35 ? 'text-red-600' : item.avgDiscountRate < 25 ? 'text-green-600' : ''}>
                    {item.avgDiscountRate.toFixed(2)}%
                  </span>
                </td>
                <td className="px-3 py-2 text-right">{item.totalCareRevenue.toLocaleString()}원</td>
                <td className="px-3 py-2 text-right">{item.totalFamilyRevenue.toLocaleString()}원</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
